import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { Button, Theme, Typography, makeStyles } from '@material-ui/core';
import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';
import { setPasswordAPIcall } from '../../../redux/actions/auth/auth.actions';
import { lightGrey } from '../../../app/theme';
import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
import HomeFooter from '../../micro/HomeFooter/HomeFooter';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';

const useStyles = makeStyles((theme: Theme) => ({
  passwordPageWrapper: {
    minHeight: 'calc(100vh - 235px)',
    padding: '17px 65px 65px',
  },
  title: {
    margin: '17px 0 49px 0',
    paddingBottom: '0.5em',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    display: 'inline-block',
    minWidth: 465,
    color: theme.palette.secondary.main,
    fontSize: 40,
    fontWeight: 600,
  },
  form: {
    width: '85%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  formColumn: {
    width: '30%',
    marginRight: 60,
    display: 'flex',
    flexDirection: 'column',
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  control: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 5,
  },
  passwordInfo: {
    fontSize: 16,
    margin: 0,
  },
  list: {
    margin: 0,
    padding: 0,
    '& > li': {
      fontSize: 16,
      marginLeft: '1em',
    },
  },
  submitContainer: {
    marginTop: 40,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  button: {
    borderRadius: 21,
    height: 42,
    minWidth: 104,
    marginLeft: 12,
  },
  images: {
    display: 'block',
    width: '100%',
  },
}));

const passwordSetSchema = Yup.object({
  password: Yup.string()
    .required('*Required field')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[A-Z])/, 'Password must include at least one uppercase character')
    .matches(/^(?=.*[0-9])/, 'Password must include at least one number')
    .matches(/^(?=.*[`~!@#$%^&*()\-_=+{}[\]\\|;:'",./?])/, 'Password must include at least one symbol')
    .test('No angle brackets', 'Password may not contain < or >', (value) => {
      return value && value.indexOf('<') < 0 && value.indexOf('>') < 0;
    }),
  confirmPassword: Yup.string()
    .required('*Required field')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

const PasswordSet = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const params = new URLSearchParams(useLocation().search);

  return (
    <>
      <HomeNavBar />
      <div className={classes.passwordPageWrapper}>
        <Typography className={classes.title}>Set Password</Typography>
        <Formik
          initialValues={{
            password: '',
            confirmPassword: '',
          }}
          validationSchema={passwordSetSchema}
          onSubmit={(values) => {
            dispatch(setPasswordAPIcall(values.password, params.get('token') ?? ''));
          }}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit} className={classes.form}>
              <div className={classes.formColumn}>
                <div className={classes.control}>
                  <TextFieldWrapper
                    form={formProps}
                    inputLabelProps={{ className: classes.inputField }}
                    type="password"
                    label="Password*"
                    formKey="password"
                    variant="outlined"
                  />
                </div>
                <div className={classes.control}>
                  <TextFieldWrapper
                    form={formProps}
                    inputLabelProps={{ className: classes.inputField }}
                    type="password"
                    label="Confirm Password*"
                    formKey="confirmPassword"
                    variant="outlined"
                  />
                </div>
              </div>
              <div className={classes.formColumn}>
                <div className={classes.passwordInfo}>
                  <Typography>The password must include at least:</Typography>
                  <ul className={classes.list}>
                    <li>Eight characters</li>
                    <li>One uppercase character</li>
                    <li>One lowercase character</li>
                    <li>One number</li>
                    <li>One special character, except &lt; and &gt;</li>
                  </ul>
                </div>
                <div className={classes.submitContainer}>
                  <Button className={classes.button} variant="contained" color="secondary" href="../login">
                    Cancel
                  </Button>
                  <Button className={classes.button} type="submit" variant="contained" color="secondary">
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <div>
        <img
          className={classes.images}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
        />
        <HomeFooter />
      </div>
    </>
  );
};

export default PasswordSet;
